import { createAsyncThunk, createSlice, PayloadAction, unwrapResult } from '@reduxjs/toolkit';
import { RootState } from './store';
import { Concept, ConceptRef } from './ConceptSlice';
import http from '../utils/http';
import enums from '../utils/enums';
import { TaxonomyTypeEnum, VersionRef } from './TaxonomyVersionSlice';

export interface CustomReferenceRole {
    name: string;
    documentation: string;
    effectiveDate: string | null;
    deprecationDate: string | null;
}
export interface PartDefinition {
    group: string;
    name: string;
    documentation: string;
    datatype: string;
    deprecationDate: string | null;
}
export interface Part {
    definition: PartDefinition;
    value: string;
}
export interface Reference {
    type: string;
    parts: Array<Part>;
    dirty: boolean | null;
    new: boolean | null;
}

export interface DtsConcept {
    concept: Concept | null;
    year: number;
}
export interface PartValue {
    partName: string;
    partValue: string;
}
export interface ReferenceState {
    selectedReference: Reference | null;
    referenceById: Reference | null;
    allReferences: Array<Reference> | null;
    allReferenceAssignments: Array<Concept> | null;
    allPartDefinitions: Array<PartDefinition> | null;
    allReferenceRoles: Array<CustomReferenceRole> | null;
    status: 'idle' | 'loading' | 'failed';
}

export interface ReferenceAssignmentsRequest {
    taxonomyType: TaxonomyTypeEnum;
    taxonomyYear: number;
    referenceId: string;
}

const initialState: ReferenceState = {
    selectedReference: null,
    referenceById: null,
    allReferences: null,
    allReferenceAssignments: null,
    allPartDefinitions: null,
    allReferenceRoles: [
        {
            name: "http://www.xbrl.org/2003/role/reference",
            documentation: "the standard concept documentation role",
            effectiveDate: null,
            deprecationDate: null
        },
        {
            name: "https://www.apps.fldfs.com/LocalGov/uas/roles/reference/dew",
            documentation: "references to hold the documentation parts for a DEW element: Account, Source, Calculation",
            effectiveDate: null,
            deprecationDate: null
        }
    ],
    status: "idle"
}

export const filterPartDefinitionsForReferenceType = function (r: Reference | null, allPartDefs: PartDefinition[]): PartDefinition[] {
    if (!r) {
        return allPartDefs;
    }
    var results: PartDefinition[] = [];
    var filterGroups: string[] = [];
    var partMap : any = {};
    var groupedParts: PartDefinition[] = [];
    for (var pd of allPartDefs) {
        var t = pd.group;
        if (partMap.hasOwnProperty(t)) {
            groupedParts = partMap[t];
        } else {
            groupedParts = [];
            partMap[t] = groupedParts;
        }
        groupedParts.push(pd);
    }
    switch (getShortTypeNameString(r.type)) {
        case "changeNote":
            filterGroups.push("http://fasb.org/cn-part/2022");
            break;
        case "dew":
            filterGroups.push("https://www.apps.fldfs.com/LocalGov/taxonomies/uas/dew/parts");
            break;
        case "urn:uas:referencerole:court-related":
            filterGroups.push( "urn:uas:referencerole:court-related" );
            break;
        default:
            filterGroups.push("https://www.apps.fldfs.com/LocalGov/taxonomies/uas/parts");
            filterGroups.push("http://www.xbrl.org/2006/ref");
            break;
    }
    if (filterGroups.length === 0 ) {
        return allPartDefs;
    }
    for (var g of filterGroups) {
        results = results.concat(partMap[g]);
    }
    return results;
}
export const getPartLabel = function (pd: PartDefinition) {
    var prefix = "";
    switch (pd.group) {
        case "https://www.apps.fldfs.com/LocalGov/taxonomies/uas/dew/parts":
            prefix = "dew: ";
            break;
        case "https://www.apps.fldfs.com/LocalGov/taxonomies/uas/parts":
        case "http://www.xbrl.org/2006/ref":
            prefix = " ";
            break;
        case "urn:uas:referencerole:court-related":
            prefix = "court-related: ";
            break;
        case "http://fasb.org/cn-part/2022":
            prefix = "change-note: ";
            break;

        default:
            prefix = pd.group + ": ";
            break;
    }
    return prefix + pd.name;
}

export const referenceHasPart = function (r: Reference | null, pname: string): boolean {
    if (!r) {
        return false;
    }
    var p: Part | undefined = r.parts.find(rp => rp.definition.name === pname);
    if (p === undefined) {
        return false;
    }
    return true;
}
 export const isShareable = function (reference: Reference | null): boolean {
    if (!reference) {
        return false;
    }
    if (referenceHasPart(reference, "UASID")) {
        return true;
    }
    if (reference.type.indexOf("instruction") > 0) {
        return false;
    }
    return false;
}

export const getShortTypeNameString = function (typeName : string ): string {
    var idxLast = typeName.lastIndexOf("/reference");
    if (idxLast < 0) {
        idxLast = typeName.lastIndexOf(':');
    } else {
        if (typeName.endsWith("/reference")) {
            return "standard";
        }
        idxLast = typeName.lastIndexOf("/reference/");
        if (idxLast > 0) {
            // This should be picking up the beginning of the name beyond /reference/
            return typeName.substring(idxLast + 11); // 11 for /reference/
        }
    }
    if (idxLast > 0) {
        return typeName.substring(idxLast + 1)
    }
    return typeName;
}

export const getShortTypeName = function (r: CustomReferenceRole): string {
    return getShortTypeNameString(r.name);
}

export const getRefMap = function (r: Reference): any {
    var rmap: any = {};
    rmap["type"] = getShortTypeNameString(r.type);
    for (var p of r.parts) {
        rmap[p.definition.name] = p.value;
    }
    return rmap;
}

export const getUasId = function (refer: Reference, rmap: any | null): string {
    if (!refer) {
        return "";
    }
    if (!rmap) {
        rmap = getRefMap(refer);
    }
    var uasid = "";
    if (rmap.hasOwnProperty("UASID")) {
        uasid = rmap["UASID"];
    }
    return uasid;
}

export const getPartValue = function (r: Reference, pname: string) : string {
    var result: string = "";
    for (var p of r.parts) {
        if (p.definition.name === pname) {
            result = p.value;
        }
    }
    return result;
}

export const getPart = function (r: Reference, pname: string): Part | null {
    var result: Part | null = null;
    for (var p of r.parts) {
        if (p.definition.name === pname) {
            result = p;
        }
    }
    return result;
}

export const getDataTypeForDefinition = function (pd: PartDefinition): string {
    var datatype: string = pd.datatype;
    if (pd.name.toLowerCase().indexOf("date") >= 0 ) {
        datatype = "XBRLI_DATEITEMTYPE";
    }
    return datatype;
}

export const getPreviousEntityName = function (r: Reference): string {
    var d: string = "";
    var n: string = "";
    r.parts.map((x: any) => {
        switch (x.definition.name) {
            case "PreviousEntityName":
                n = x.value;
                break;

            case "NameChangeDate":
                d = x.value;
                break;
        }
    });
    return n;
}

export const loadAllTaxonomyReferences = createAsyncThunk(
    "Taxonomy/LoadAllReferences",
    async (vRef : VersionRef): Promise<Reference[]> => {
        try {
            const result = await http<undefined, Reference[]>({
                path: `/taxonomy/${vRef.type}/allReferences/${vRef.year}`,
            });

            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return [];
            }
        } catch (ex) {
            console.log("exception value", ex);
            return [];
        }
    });

export const loadTaxonomyReference = createAsyncThunk(
    "Taxonomy/LoadReference",
    async (referenceId : string): Promise<Reference | null> => {
        try {
            console.log(referenceId);
            const result = await http<undefined, Reference | null>({
                path: `/taxonomy/AFR/reference/${referenceId}`,
            });

            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            console.log("exception value", ex);
            return null;
        }
    });

export const loadAllPartDefinitions = createAsyncThunk(
    "Taxonomy/LoadAllPartDefinitions",
    async (vRef: VersionRef): Promise<PartDefinition[]> => {
        try {
            const result = await http<undefined, PartDefinition[]>({
                path: `/taxonomy/${vRef.type}/partDefinitions/${vRef.year}`,
            });

            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return [];
            }
        } catch (ex) {
            console.log("exception value", ex);
            return [];
        }
    });

export const loadAllReferenceRoles = createAsyncThunk(
    "Taxonomy/LoadAllReferenceRoles",
    async (vRef: VersionRef): Promise<CustomReferenceRole[]> => {
        try {
            const result = await http<undefined, CustomReferenceRole[]>({
                path: `/taxonomy/${vRef.type}/referenceRoles/${vRef.year}`,
            });

            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return [];
            }
        } catch (ex) {
            console.log("exception value", ex);
            return [];
        }
    });

export const loadAllReferenceAssignments = createAsyncThunk(
    "Taxonomy/LoadAllReferenceAssignments",
    async (req: ReferenceAssignmentsRequest): Promise<Concept[]> => {
        try {
            const result = await http<undefined, Concept[]>({
                path: `/taxonomy/${req.taxonomyType}/allReferenceAssignments/${req.taxonomyYear}/${req.referenceId}`,
            });

            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return [];
            }
        } catch (ex) {
            console.log("exception value", ex);
            return [];
        }
    });

export const taxonomyReferenceSlice = createSlice({
    name: 'TaxonomyReference',
    initialState,
    reducers: {
        resetReferenceState: (state: any) => {
            return initialState;
        },
        setSelectedReference: (state: any, action: any) => {
            state.selectedReference = action.payload;
            state.allReferenceAssignments = null;
        },
        clearSelectedReference: (state: any) => {
            state.selectedReference = null;
            state.allReferenceAssignments = null;
        },
        clearSelectedReferenceDirty: (state: any) => {
            if (state.selectedReference) {
                state.selectedReference.dirty = false;
            }
        },
        clearSelectedReferenceAssignments: (state: any) => {
            state.allReferenceAssignments = null;
        },
        updateSelectedReferencePart: {
            reducer(state: any, action: any) {
                var pv : PartValue = action.payload;
                if (state.selectedReference) {
                    if (pv.partValue.length === 0) {
                        // we remove the part
                        var newParts : any = [];
                        for (var nextp of state.selectedReference.parts) {
                            if (nextp.definition.name !== pv.partName) {
                                newParts.push({ ...nextp });
                            }
                        }
                        state.selectedReference.parts = newParts;
                    } else {
                        var p: Part | null = getPart(state.selectedReference, pv.partName);
                        if (p) {
                            p.value = pv.partValue;
                        }
                    }
                    state.selectedReference.dirty = true;
                }
            },
            prepare(payload: PartValue) {
                return {
                    payload
                }
            }
        },
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(loadAllTaxonomyReferences.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadAllTaxonomyReferences.fulfilled, (state: any, action: any) => {
                state.allReferences = action.payload;
                state.allReferenceAssignments = null;
                state.status = "idle";
            })
            .addCase(loadTaxonomyReference.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadTaxonomyReference.fulfilled, (state: any, action: any) => {
                state.referenceById = action.payload;
                state.status = "idle";
            })
            .addCase(loadAllReferenceAssignments.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadAllReferenceAssignments.fulfilled, (state: any, action: any) => {
                state.allReferenceAssignments = action.payload;
                state.status = "idle";
            })
            .addCase(loadAllPartDefinitions.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadAllPartDefinitions.fulfilled, (state: any, action: any) => {
                state.allPartDefinitions = action.payload;
                state.status = "idle";
            })
            .addCase(loadAllReferenceRoles.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadAllReferenceRoles.fulfilled, (state: any, action: any) => {
                state.allReferenceRoles = action.payload;
                state.status = "idle";
            })
    },
});

export const { setSelectedReference, clearSelectedReference, clearSelectedReferenceDirty, updateSelectedReferencePart, resetReferenceState, clearSelectedReferenceAssignments } = taxonomyReferenceSlice.actions;

export const getTaxonomyReference = (state: RootState) => state.taxonomyReference ? state.taxonomyReference : null;

export const selectCurrentReference = (state: RootState) => state.taxonomyReference ? state.taxonomyReference.selectedReference : null;

export const selectAllReferences = (state: RootState) => (state.taxonomyReference && state.taxonomyReference.allReferences) ? state.taxonomyReference.allReferences : [];

export const selectReferenceAssignments = (state: RootState) => (state.taxonomyReference && state.taxonomyReference.allReferenceAssignments)
                                                        ? state.taxonomyReference.allReferenceAssignments
                                                        : [];
export const selectAllPartDefinitions = (state: RootState) => (state.taxonomyReference && state.taxonomyReference.allPartDefinitions)
    ? state.taxonomyReference.allPartDefinitions
    : [];

export const selectAllReferenceRoles = (state: RootState) => (state.taxonomyReference && state.taxonomyReference.allReferenceRoles)
    ? state.taxonomyReference.allReferenceRoles
    : [];

export const selectIdReference = (state: RootState) => (state.taxonomyReference && state.taxonomyReference.referenceById)
    ? state.taxonomyReference.referfenceById
    : null;

export default taxonomyReferenceSlice.reducer;